import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Avatar from '../Avatar';
import SuggestIdeaModal from '../SuggestIdeaModal';
import SurpriseGiftModal from '../SurpriseGiftModal';

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

export default function MembersTab({ group, members, invitations, user, onMembersChanged, onInvitationsChanged, notify }) {
    const navigate = useNavigate();
    const [showInvite, setShowInvite] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    // People from the user's OTHER groups — the web's stand-in for the iOS
    // app's contact search (browsers can't read the OS address book, but names
    // and emails of people you already share a group with are data the app
    // shows you anyway). null = not loaded yet.
    const [knownPeople, setKnownPeople] = useState(null);
    // Set right after clicking a suggestion so the filled-in email doesn't
    // immediately re-open the suggestion list (same trick as the iOS view).
    const [justPicked, setJustPicked] = useState(false);
    // The member we're suggesting a gift idea to (opens SuggestIdeaModal).
    const [suggestTo, setSuggestTo] = useState(null);
    // The member we're adding a surprise gift for (opens SurpriseGiftModal).
    const [surpriseTo, setSurpriseTo] = useState(null);

    const myMembership = members.find((m) => m.id === user?.id);
    const isAdmin = myMembership?.pivot?.role === 'admin';
    // Prefer the authoritative `viewer_can_invite` the API now returns — it
    // includes the creator fallback (a legacy creator whose pivot defaulted to
    // 'member' can still invite; see listful-api docs/FEEDBACK_PLAN.md §3), which
    // the local pivot calc misses. Fall back to the local calc for older payloads.
    // (`can_invite` arrives from MySQL as 0/1, so coerce to a real boolean or a
    // literal "0" would leak into the {canInvite && …} JSX below.)
    const canInvite = group?.viewer_can_invite ?? (isAdmin || !!myMembership?.pivot?.can_invite);

    // Load the suggestion pool once, when the invite modal first opens:
    // every member of every group the user belongs to, deduped by email.
    useEffect(() => {
        if (!showInvite || knownPeople !== null) return;
        let cancelled = false;
        api.get('/groups')
            .then((response) => {
                if (cancelled) return;
                const byEmail = new Map();
                for (const g of response.data) {
                    for (const person of g.members || []) {
                        if (person.email && !byEmail.has(person.email)) {
                            byEmail.set(person.email, person);
                        }
                    }
                }
                setKnownPeople([...byEmail.values()]);
            })
            .catch(() => {
                if (!cancelled) setKnownPeople([]); // no suggestions; typing still works
            });
        return () => {
            cancelled = true;
        };
    }, [showInvite, knownPeople]);

    // Filtered as-you-type, excluding people who can't sensibly be invited:
    // current members, already-invited emails, and the user themself.
    const suggestions = useMemo(() => {
        const q = inviteEmail.trim().toLowerCase();
        if (justPicked || !q || !knownPeople) return [];
        const memberEmails = new Set(members.map((m) => m.email));
        const invitedEmails = new Set(invitations.map((i) => i.email));
        return knownPeople
            .filter(
                (p) =>
                    p.id !== user?.id &&
                    !memberEmails.has(p.email) &&
                    !invitedEmails.has(p.email) &&
                    (p.name?.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
            )
            .slice(0, 6);
    }, [inviteEmail, justPicked, knownPeople, members, invitations, user]);

    const pickSuggestion = (person) => {
        setJustPicked(true);
        setInviteEmail(person.email);
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/groups/${group.id}/invitations`, { email: inviteEmail });
            setInviteEmail('');
            setJustPicked(false);
            setShowInvite(false);
            notify('success', 'Invitation sent.');
            onInvitationsChanged();
        } catch (err) {
            notify('error', errText(err, 'Failed to send invitation.'));
        }
    };

    const handleResendInvitation = async (invitationId) => {
        try {
            await api.post(`/invitations/${invitationId}/resend`);
            notify('success', 'Invitation resent.');
            onInvitationsChanged();
        } catch (err) {
            notify('error', errText(err, 'Failed to resend invitation.'));
        }
    };

    const handleCancelInvitation = async (invitationId) => {
        try {
            await api.delete(`/invitations/${invitationId}`);
            notify('success', 'Invitation cancelled.');
            onInvitationsChanged();
        } catch (err) {
            notify('error', errText(err, 'Failed to cancel invitation.'));
        }
    };

    const handleRemoveMember = async (userId, name) => {
        if (!window.confirm(`Remove ${name} from this group?`)) return;
        try {
            await api.delete(`/groups/${group.id}/members/${userId}`);
            notify('success', `${name} removed from the group.`);
            onMembersChanged();
        } catch (err) {
            notify('error', errText(err, 'Failed to remove member.'));
        }
    };

    const handleChangeRole = async (userId, role, canInviteFlag) => {
        try {
            await api.put(`/groups/${group.id}/members/${userId}/role`, {
                role,
                can_invite: canInviteFlag ? 1 : 0,
            });
            onMembersChanged();
        } catch (err) {
            notify('error', errText(err, 'Failed to update member.'));
        }
    };

    const copyInviteLink = (token) => {
        const url = `${window.location.origin}/invite/${token}`;
        navigator.clipboard.writeText(url);
        notify('success', 'Invite link copied.');
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Group Members ({members.length})</h2>
                {canInvite && (
                    <button
                        onClick={() => setShowInvite(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                    >
                        + Invite Member
                    </button>
                )}
            </div>

            {/* Pending invitations */}
            {invitations.length > 0 && (
                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 mb-2">Pending Invitations</h3>
                    <div className="space-y-2">
                        {invitations.map((inv) => (
                            <div key={inv.id} className="bg-white rounded-lg shadow-sm p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                                    <p className="text-xs text-gray-500">
                                        Expires {new Date(inv.expires_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => copyInviteLink(inv.token)} className="text-sm text-blue-600 hover:text-blue-700">
                                        Copy Link
                                    </button>
                                    {canInvite && (
                                        <button onClick={() => handleResendInvitation(inv.id)} className="text-sm text-blue-600 hover:text-blue-700">
                                            Resend
                                        </button>
                                    )}
                                    {canInvite && (
                                        <button onClick={() => handleCancelInvitation(inv.id)} className="text-sm text-red-600 hover:text-red-700">
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Members list */}
            <div className="space-y-2">
                {members.map((member) => {
                    const isSelf = member.id === user?.id;
                    const memberIsAdmin = member.pivot?.role === 'admin';
                    const canManage = isAdmin && !isSelf;

                    return (
                        <div key={member.id} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between">
                            {/* Tapping a member opens their profile, scoped to THIS
                                group so you see what they shared here. Only the
                                identity block is the tap target — the role select
                                and Remove sit outside it, or an admin adjusting a
                                role would navigate away mid-action. */}
                            <button
                                onClick={() => navigate(`/person/${member.id}?group=${group.id}`)}
                                className="flex items-center gap-3 text-left rounded-md hover:bg-gray-50 -m-1 p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                title={`View ${member.name}'s wishes`}
                            >
                                <Avatar name={member.name} url={member.avatar_url} color={member.avatar_color} seed={member.id} size={40} />
                                <div>
                                    <p className="font-medium text-gray-900">
                                        {member.name} {isSelf && <span className="text-xs text-gray-400">(You)</span>}
                                    </p>
                                    <p className="text-sm text-gray-500">{member.email}</p>
                                </div>
                            </button>

                            <div className="flex items-center gap-3">
                                {!isSelf && (
                                    <button
                                        onClick={() => setSuggestTo(member)}
                                        className="text-sm text-blue-600 hover:text-blue-700"
                                        title={`Recommend a gift to ${member.name}`}
                                    >
                                        Recommend a gift
                                    </button>
                                )}
                                {!isSelf && (
                                    <button
                                        onClick={() => setSurpriseTo(member)}
                                        className="text-sm text-amber-700 hover:text-amber-800"
                                        title={`Add a surprise gift for ${member.name}, hidden from them`}
                                    >
                                        Surprise 🎁
                                    </button>
                                )}
                                {canManage ? (
                                    <select
                                        value={member.pivot?.role}
                                        onChange={(e) => handleChangeRole(member.id, e.target.value, member.pivot?.can_invite)}
                                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                                    >
                                        <option value="member">Member</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                ) : (
                                    memberIsAdmin && (
                                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Admin</span>
                                    )
                                )}

                                {canManage && !memberIsAdmin && (
                                    <label className="flex items-center gap-1 text-xs text-gray-600">
                                        <input
                                            type="checkbox"
                                            checked={!!member.pivot?.can_invite}
                                            onChange={(e) => handleChangeRole(member.id, member.pivot?.role, e.target.checked)}
                                        />
                                        Can invite
                                    </label>
                                )}

                                {canManage && (
                                    <button onClick={() => handleRemoveMember(member.id, member.name)} className="text-sm text-red-600 hover:text-red-700">
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Invite modal */}
            {showInvite && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-4">Invite a Member</h3>
                        <form onSubmit={handleInvite}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={inviteEmail}
                                    onChange={(e) => {
                                        setInviteEmail(e.target.value);
                                        setJustPicked(false);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="friend@example.com"
                                    autoComplete="off"
                                />
                                {/* Suggestions from the user's other groups (mirrors the
                                    iOS contact suggestions — see ContactSearch.swift). */}
                                {suggestions.length > 0 && (
                                    <div className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100 shadow-sm">
                                        <p className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 rounded-t-md">
                                            From your groups
                                        </p>
                                        {suggestions.map((person) => (
                                            <button
                                                key={person.email}
                                                type="button"
                                                onClick={() => pickSuggestion(person)}
                                                className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-blue-50"
                                            >
                                                <Avatar
                                                    name={person.name}
                                                    url={person.avatar_url}
                                                    color={person.avatar_color}
                                                    seed={person.id}
                                                    size={28}
                                                />
                                                <span>
                                                    <span className="block text-sm font-medium text-gray-900">{person.name}</span>
                                                    <span className="block text-xs text-gray-500">{person.email}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowInvite(false);
                                        setInviteEmail('');
                                        setJustPicked(false);
                                    }}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                    Send Invite
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Suggest an idea to a member — recipient + this group prefilled. */}
            {suggestTo && (
                <SuggestIdeaModal
                    prefill={{ recipientUserId: suggestTo.id, groupId: group.id }}
                    notify={notify}
                    onClose={() => setSuggestTo(null)}
                    onSaved={() => setSuggestTo(null)}
                />
            )}

            {/* Add a surprise gift for a member — a wish hidden from them, visible
                to the rest of the group. Recipient fixed; this group prefilled. */}
            {surpriseTo && (
                <SurpriseGiftModal
                    recipient={{ id: surpriseTo.id, name: surpriseTo.name }}
                    prefill={{ groupId: group.id }}
                    notify={notify}
                    onClose={() => setSurpriseTo(null)}
                    onSaved={() => setSurpriseTo(null)}
                />
            )}
        </div>
    );
}
